/**
 * JobDetail Component
 *
 * Drill-down view for a single job showing:
 * - Job metadata and status
 * - Related candidates (sourced and ATS)
 * - Outreach messages and reply threads
 * - Sourcing runs history
 *
 * Uses service-role queries to bypass RLS.
 *
 * ------------------------------------------------------------------
 * Migrated for "Implement RTK Query hooks and caching for all API
 * endpoints": the job metadata itself previously came from a hand-rolled
 * `useEffect` + local `useState` that fabricated a mock `AdminJobInfo`
 * from the route's `jobId` param behind a `// TODO: Call GET
 * /api/admin/jobs/:jobId` comment. Now fetched via `useAdminJobDetail()`
 * (`@/lib/api/apiClient`) — a TanStack Query hook, Zod-validated
 * response, seeded with `location.state`'s job (from `JobsList`'s row
 * click) as `initialData` so there's no loading flash for data the
 * caller already has.
 *
 * UPDATE (GitHub issue #498, "E2E: Sourcing & outreach — pipeline
 * tracking, email management, replies"): the separate `candidates` /
 * `outreachMessages` mock-`useEffect` blocks this comment used to
 * describe have been replaced by the unified `Pipeline` tab below, which
 * renders `@/components/admin/CandidateOutreachPipeline` — that
 * component owns its own (still-mocked-backend, see its module
 * doc-comment) data fetching via `useAdminJobPipeline(jobId)` instead of
 * component-local `useState`/`useEffect`. The "Sourcing Runs" tab remains
 * a placeholder (unrelated to this issue's scope).
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useAdminJobDetail, reportApiError } from '@/lib/api/apiClient';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminJobInfo } from '@/lib/validation/admin';
import CandidateOutreachPipeline from '@/components/admin/CandidateOutreachPipeline';

interface JobDetailState {
  activeTab: 'pipeline' | 'sourcing';
}

export default function JobDetail() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  const jobFromState = (location.state as { job?: AdminJobInfo })?.job;

  const {
    data: job,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useAdminJobDetail(jobId, jobFromState);

  const [state, setState] = useState<JobDetailState>({
    activeTab: 'pipeline',
  });

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load job details');
    }
  }, [isError, error]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 space-y-4" aria-busy="true">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-4 md:grid-cols-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md">
          <div className="text-lg font-semibold text-destructive">Error Loading Job</div>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'Job not found'}
          </p>
          <div className="mt-4 flex gap-2 justify-center">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/admin/jobs')}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
            >
              Back to Jobs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-blue-100 text-blue-800';
      case 'PAUSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/admin/jobs')}
            className="px-3 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium mb-4"
          >
            ← Back to Jobs
          </button>

          <div>
            <h1 className="text-2xl font-bold mb-2">{job.title}</h1>
            <div className="flex gap-4 items-center flex-wrap">
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                  job.status
                )}`}
              >
                {job.status}
              </span>
              <span className="text-sm text-muted-foreground">By: {job.owner_name}</span>
              <span className="text-sm text-muted-foreground">
                Created: {new Date(job.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Key Metrics */}
        <section className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Funnel Metrics</h2>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Sourced</p>
              <p className="text-2xl font-bold">{job.sourced_count}</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Screened</p>
              <p className="text-2xl font-bold">{job.screened_count}</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Outreach Sent</p>
              <p className="text-2xl font-bold">{job.outreach_sent}</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Replies</p>
              <p className="text-2xl font-bold">{job.replies_received}</p>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <section className="mb-8">
          <div className="flex gap-2 border-b border-border mb-4">
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: 'pipeline' }))}
              data-testid="job-detail-tab-pipeline"
              className={`px-4 py-2 font-medium border-b-2 ${
                state.activeTab === 'pipeline'
                  ? 'border-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              Candidate Pipeline
            </button>
            <button
              onClick={() => setState((prev) => ({ ...prev, activeTab: 'sourcing' }))}
              data-testid="job-detail-tab-sourcing"
              className={`px-4 py-2 font-medium border-b-2 ${
                state.activeTab === 'sourcing'
                  ? 'border-foreground'
                  : 'border-transparent text-muted-foreground'
              }`}
            >
              Sourcing Runs
            </button>
          </div>

          {/* Candidate Pipeline Tab — see @/components/admin/CandidateOutreachPipeline
              for status tabs (Queued/Sent/Replied/Bounced), thread panel,
              bulk operations, reply management, and auto-followup toggles. */}
          {state.activeTab === 'pipeline' && <CandidateOutreachPipeline jobId={job.uuid} />}

          {/* Sourcing Runs Tab */}
          {state.activeTab === 'sourcing' && (
            <div className="text-center py-8 text-muted-foreground">
              <p>TODO: Implement sourcing runs view</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
